import { skipToken } from '@reduxjs/toolkit/query'
import React, { useEffect, useState } from 'react'
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Avatar } from '../../components/avatar/Avatar'
import { VendorImage } from '../../components/avatar/vendorImage'
import { Button } from '../../components/button'
import { ChartCard } from '../../components/cards/ChartCard'
import CountCard from '../../components/cards/CountCard'
import { Status } from '../../components/cards/statusTag'
import Icon from '../../components/icon'
import { DateFilterNav } from '../../components/inputs/dateFilterNav'
import Dropdown from '../../components/inputs/dropdown'
import { SearchInput } from '../../components/inputs/searchInput'
import { TableComponent } from '../../components/table'
import { PageHeader } from '../../components/typography/PageHeader'
import { CountCardContainer } from '../../components/wrapper/CountCardsContainer'
import { NUMBER_OF_ITEMS_PER_PAGE } from '../../constants'
import {
  formatCurrency,
  getPageFromSkip,
  getSkipFromPage,
  prettyFormatNumber,
} from '../../helpers'
import { useAppSelector } from '../../hooks'
import { usePermissions } from '../../hooks/usePermissions'
import { ReportPeriodRange } from '../../redux-toolkits/reports/reports.type'
import {
  useGetAllVendorsMetricsQuery,
  useGetAllVendorsQuery,
  useReInviteVendorMutation,
  useUpdateVendorDetailsMutation,
} from '../../redux-toolkits/vendors/vendors.slice'
import { VendorsQuery } from '../../redux-toolkits/vendors/vendors.types'
import { QueryProps } from '../../redux-toolkits/shared/shared.type'
const AllVendors: React.FC = () => {
  const [query, setQuery] = useState<VendorsQuery>()
  const [metricsQuery, setMetricsQuery] = useState<QueryProps>()

  const [searchParams, setSearchParams] = useSearchParams()

  const { selectedFulfilmentCenters } = useAppSelector((state) => state.auth)

  const navigate = useNavigate()
  const { hasPermissions } = usePermissions()

  const { data: vendorMetrics, isFetching: fetchingVendorMetrics } =
    useGetAllVendorsMetricsQuery(
      metricsQuery
        ? {
            ...metricsQuery,
            fulfillmentCentreId:
              selectedFulfilmentCenters.map((c) => c._id).join(',') ||
              undefined,
          }
        : skipToken,
      {
        skip: selectedFulfilmentCenters.length === 0,
      },
    )

  const { data, isFetching } = useGetAllVendorsQuery(
    query
      ? {
          ...query,
          fulfillmentcenterId:
            selectedFulfilmentCenters.map((c) => c._id).join(',') || undefined,
        }
      : skipToken,
    {
      skip: selectedFulfilmentCenters.length === 0,
    },
  )

  const [updateVendor, { isLoading: updatingVendor }] =
    useUpdateVendorDetailsMutation()

  const [resendInvitation, { isLoading: resendingInvitation }] =
    useReInviteVendorMutation()

  useEffect(() => {
    setQuery({
      skip: Number(searchParams.get('skip') || 0),
      limit: 20,
      search: searchParams.get('search') || undefined,
    })

    const custDate = searchParams.get('isCustomDateRange')
      ? searchParams.get('isCustomDateRange') === 'true'
      : false

    setMetricsQuery({
      isCustomDateRange: custDate,
      filter: custDate
        ? undefined
        : (searchParams.get('filter') as ReportPeriodRange) || 'today',
      startDate: custDate
        ? searchParams.get('startDate') || undefined
        : undefined,
      endDate: custDate ? searchParams.get('endDate') || undefined : undefined,
    })
  }, [searchParams])

  return (
    <div className="flex flex-col gap-10">
      <div className="flex flex-wrap max-md:w-full items-center justify-between gap-6">
        <PageHeader title="Creators" className="!mb-0" />

        <DateFilterNav
          filter={metricsQuery?.filter ?? 'custom'}
          isCustomDateRange={metricsQuery?.isCustomDateRange ?? false}
          startDate={metricsQuery?.startDate}
          endDate={metricsQuery?.endDate}
          setFilter={(filter) => {
            setSearchParams({
              ...Object.fromEntries(searchParams),
              filter: filter.filter ?? '',
              isCustomDateRange: String(filter.isCustomDateRange),
              endDate: filter.endDate ?? '',
              startDate: filter.startDate ?? '',
            })
          }}
        />
      </div>

      <CountCardContainer className="!my-0">
        <CountCard
          isCurrency={false}
          text="Total Creators"
          count={vendorMetrics?.data.totalVendors ?? 0}
          loading={fetchingVendorMetrics}
        />
        <CountCard
          isCurrency={false}
          text="Total Active Creators"
          count={vendorMetrics?.data.activeVendors ?? 0}
          loading={fetchingVendorMetrics}
        />
        <CountCard
          isCurrency={true}
          text="Total Revenue"
          count={vendorMetrics?.data.totalRevenue ?? 0}
          loading={fetchingVendorMetrics}
        />
      </CountCardContainer>

      <ChartCard title="Top Creators by Revenue" value={undefined}>
        <ResponsiveContainer width={'100%'} height={240} className={'mt-6'}>
          <BarChart
            data={
              (vendorMetrics?.data?.vendorMetrics?.length ?? 0) > 0
                ? vendorMetrics?.data?.vendorMetrics
                    ?.slice(0, 11)
                    ?.map((entry) => {
                      return {
                        label: entry.businessName,
                        revenue: entry.totalRevenue,
                        orders: entry.totalOrders,
                      }
                    })
                : [{ label: '', revenue: 0, orders: 0 }]
            }
            margin={{
              top: 0,
              right: 0,
              left: 0,
              bottom: 0,
            }}
          >
            <CartesianGrid
              strokeDasharray="3 0"
              vertical={true}
              stroke="#EAEAEA"
            />

            <YAxis
              dataKey="revenue"
              axisLine={false}
              tickLine={false}
              type="number"
              style={{
                fontSize: '12px',
                fontWeight: 'normal',
                color: '#555555',
              }}
              tickFormatter={(val) => prettyFormatNumber(val)}
            />

            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              interval={0}
              style={{
                fontSize: '12px',
                fontWeight: 'normal',
                color: '#555555',
              }}
              tickFormatter={(val) => val.slice(0, 8) + '...'}
            />

            <Tooltip
              content={({ payload }) => {
                if (!payload?.length) return null
                const dateLabel = payload[0]?.payload?.label
                const revenue = payload[0]?.payload?.revenue
                const totalOrder = payload[0]?.payload?.orders

                return (
                  <div className="flex flex-col gap-2 rounded-[8px] bg-[#F3F3F3] p-2 text-left text-xs max-w-[250px]">
                    <p className="text-sec-black">{dateLabel}</p>
                    <p className="font-bold text-base text-black">
                      {payload ? `${formatCurrency(revenue ?? 0, {})}` : ''}
                    </p>
                    <p className="text-sec-black">
                      {payload ? `Total order count: ${totalOrder}` : 0}
                    </p>
                  </div>
                )
              }}
              cursor={{
                stroke: '#F45D2C',
                strokeWidth: 1,
                strokeDasharray: '8 8',
                fill: 'transparent',
              }}
            />

            <Bar
              type="linear"
              dataKey="revenue"
              stroke={undefined}
              strokeWidth={2}
              fillOpacity={1}
              fill="#F45D2C"
              maxBarSize={24}
              radius={[6, 6, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <div className="mb-10 flex-wrap flex items-center gap-4 justify-between">
        <SearchInput
          placeholder="Search creator"
          searchFunction={(search) => {
            setSearchParams({
              ...Object.fromEntries(searchParams),
              search: search,
              skip: '0',
            })
          }}
          className="!w-full md:!max-w-[400px]"
          initialValue={searchParams.get('search') || undefined}
        />

        <div className="flex flex-wrap items-center gap-3">
          {hasPermissions(['update_vendor']) ? (
            <Button
              label="Arrange creators"
              onClick={() => {
                navigate('/creators/curation')
              }}
              size="medium"
            />
          ) : undefined}

          <Button
            label="Invite Creator"
            onClick={() => {
              navigate('/creators/invite')
            }}
            primary
            size="medium"
          />
        </div>
      </div>

      <div className="w-full overflow-x-scroll">
        <TableComponent
          headers={[
            'Business Name',
            'Status',
            'Total Orders Received',
            'Total Sales',
            'Actions',
          ]}
          rows={
            data?.data?.vendors?.map((vendor) => {
              return {
                id: vendor._id,
                content: [
                  <NavLink
                    to={`/creators/${vendor._id}`}
                    className={`flex gap-2 items-center`}
                    key={`vendor-${vendor._id}-name`}
                  >
                    {vendor.logo ? (
                      <VendorImage img={vendor.logo} />
                    ) : (
                      <Avatar
                        name={`${vendor?.businessName}`}
                        useRandomBgColor
                      />
                    )}
                    <span className="hover:underline ">
                      {vendor.businessName}
                    </span>
                  </NavLink>,
                  <Status
                    text={vendor?.status ?? ''}
                    type={vendor?.status === 'active' ? 'success' : 'fail'}
                    key={`vendor-${vendor._id}-status`}
                  />,
                  vendor?.totalOrders ?? '',
                  formatCurrency(vendor?.totalSales ?? 0),
                  <Dropdown
                    key={`${vendor._id}-controls`}
                    menuButton={
                      <Icon id="ellipses" height={18} width={18} className="" />
                    }
                    closeOnClick={false}
                    menuItems={[
                      {
                        name: 'Resend invitation',
                        value: '',
                        hidden: vendor?.status === 'active',
                        onClick: async () => {
                          await resendInvitation({ id: vendor._id })
                        },
                      },
                      {
                        name:
                          vendor.status === 'active'
                            ? 'Deactivate'
                            : 'Activate',
                        value: '',
                        hidden: false,
                        onClick: async () => {
                          await updateVendor({
                            id: vendor._id,
                            data: {
                              status:
                                vendor.status === 'active'
                                  ? 'inactive'
                                  : 'active',
                            },
                          })
                        },
                      },
                    ]}
                    loading={resendingInvitation || updatingVendor}
                  />,
                ],
              }
            }) ?? []
          }
          name="vendors"
          loading={isFetching}
          isEmpty={!data?.data || data.data.vendors.length < 1}
          totalDataCount={data?.data.count}
          currentPage={getPageFromSkip(query?.skip ?? 0, query?.limit ?? 20)}
          pageLimit={NUMBER_OF_ITEMS_PER_PAGE}
          onPageChange={(page) => {
            setSearchParams({
              ...Object.fromEntries(searchParams),
              skip: `${getSkipFromPage(page, query?.limit ?? 20)}`,
            })
          }}
        />
      </div>
    </div>
  )
}

export default AllVendors
